/**
 * MKS-393 — TanStack Query hooks for the merch surfaces.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  downloadMerchExport,
  fetchMerchOverview,
  fetchPersonalDetails,
  fetchUserMerchSizes,
  updatePersonalDetails,
  updateUserMerchSizes,
  type MerchSizesPatch,
  type PersonalDetailsPatch,
} from '../services/api/merchSizes';

export const PERSONAL_DETAILS_KEY = ['personal-details'] as const;
export const MERCH_OVERVIEW_KEY = ['merch-overview'] as const;

/** The authenticated user's own birth date and merch sizes. */
export function usePersonalDetails() {
  return useQuery({
    queryKey: PERSONAL_DETAILS_KEY,
    queryFn: fetchPersonalDetails,
    staleTime: 5 * 60 * 1000,
    select: (response) => response.data,
  });
}

export function useUpdatePersonalDetails() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (patch: PersonalDetailsPatch) => updatePersonalDetails(patch),
    onSuccess: (response) => {
      // Write the server's answer straight into the cache: the PATCH returns
      // the full post-write payload, so a refetch would only re-ask for what
      // we already hold.
      queryClient.setQueryData(PERSONAL_DETAILS_KEY, response);
    },
    onError: (error) => onError(error, "Couldn't save your details"),
  });
}

/** HR/admin read of one person's sizes (the user detail page card). */
export function useUserMerchSizes(userId: number) {
  return useQuery({
    queryKey: ['user-merch-sizes', userId],
    queryFn: () => fetchUserMerchSizes(userId),
    enabled: Number.isFinite(userId) && userId > 0,
    select: (response) => response.data,
  });
}

export function useUpdateUserMerchSizes(userId: number) {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (patch: MerchSizesPatch) => updateUserMerchSizes(userId, patch),
    onSuccess: (response) => {
      queryClient.setQueryData(['user-merch-sizes', userId], response);
      // The HR overview shows this person's row, so it is now stale.
      queryClient.invalidateQueries({ queryKey: MERCH_OVERVIEW_KEY });
    },
    onError: (error) => onError(error, "Couldn't save the merch sizes"),
  });
}

/** The HR overview. `missingOnly` is part of the key so both views cache. */
export function useMerchOverview(missingOnly: boolean) {
  return useQuery({
    queryKey: [...MERCH_OVERVIEW_KEY, missingOnly],
    queryFn: () => fetchMerchOverview(missingOnly),
    select: (response) => response.data,
  });
}

/**
 * Download an export and hand it to the browser. The filter is passed through
 * so the file matches the table the person is looking at.
 */
export function useMerchExport() {
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ format, missingOnly }: { format: 'xlsx' | 'csv'; missingOnly: boolean }) =>
      downloadMerchExport(format, missingOnly),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    onError: (error) => onError(error, "Couldn't download the export"),
  });
}
